/** One control on the process start form. JSON names are PascalCase. */
export type ParameterFieldType =
  | 'date'
  | 'checkbox'
  | 'number'
  | 'text'
  | 'select'
  | 'regions'
  | 'field'
  | 'polygon'
  | 'checks';

export interface ParameterFieldOption {
  id: string;
  label: string;
}

export interface ParameterFieldSchema {
  name: string;
  type: ParameterFieldType;
  labelKey: string;
  required?: boolean;
  defaultValue?: string;
  options?: readonly ParameterFieldOption[];
}

const SOURCE: readonly ParameterFieldOption[] = [
  { id: '', label: 'PRODUCT_SOURCE_DEFAULT_SENTINEL' },
  { id: 'Sentinel2', label: 'Sentinel-2' },
  { id: 'Landsat', label: 'Landsat' },
];

const RENDER_MODE: readonly ParameterFieldOption[] = [
  { id: 'StationCells', label: 'RENDER_MODE_STATION_CELLS' },
  { id: 'NearestNeighbor', label: 'RENDER_MODE_NEAREST' },
];

const INDEX_TYPES: readonly ParameterFieldOption[] = [
  'ARVI', 'EVI', 'GNDVI', 'MNDWI', 'NDMI', 'NDVI', 'NDWI', 'ORVI', 'OSAVI',
].map(id => ({ id, label: id }));

const DATE = (name: string, labelKey: string, required = false): ParameterFieldSchema =>
  ({ name, type: 'date', labelKey, required });

const CHECK = (name: string, labelKey: string): ParameterFieldSchema =>
  ({ name, type: 'checkbox', labelKey, defaultValue: 'false' });

const NUMBER = (name: string, labelKey: string): ParameterFieldSchema =>
  ({ name, type: 'number', labelKey });

const TEXT = (name: string, labelKey: string, required = false): ParameterFieldSchema =>
  ({ name, type: 'text', labelKey, required });

const ERA_DATES: ParameterFieldSchema[] = [
  DATE('StartDate', 'Start date', true),
  DATE('EndDate', 'End date', true),
];

const NO_FIELDS: ParameterFieldSchema[] = [];

const BY_KEY: Readonly<Record<string, readonly ParameterFieldSchema[]>> = {
  'weather.load-era-raw': [
    ...ERA_DATES,
    CHECK('SkipReports', 'SKIP_REPORTS'),
    CHECK('Force', 'FORCE_REFETCH'),
  ],
  'weather.maintain-era-raw': NO_FIELDS,
  'weather.download-era-netcdf': [...ERA_DATES, CHECK('Force', 'FORCE_REFETCH')],
  'weather.import-era-netcdf': [...ERA_DATES, CHECK('SkipReports', 'SKIP_REPORTS')],
  'weather.calculate-era-reports': ERA_DATES,
  'weather.fill-era-sat': [
    DATE('StartDate', 'Start date'),
    DATE('EndDate', 'End date'),
  ],
  'weather.refresh-era-report-tiles': [
    DATE('StartDate', 'Start date'),
    DATE('EndDate', 'End date'),
  ],
  'weather.generate-era-deviation-tiles': [
    DATE('StartDate', 'Start date'),
    DATE('EndDate', 'End date'),
    CHECK('ForceRecreate', 'FORCE_RECREATE'),
  ],
  'weather.generate-era-tiles': [
    ...ERA_DATES,
    CHECK('ForceRecreate', 'FORCE_RECREATE'),
    { name: 'RenderMode', type: 'select', labelKey: 'RENDER_MODE', defaultValue: 'StationCells', options: RENDER_MODE },
  ],
  'weather.check-ibm-day-data': qualityFields(),
  'weather.check-era5-day-data': qualityFields(),
  'weather.check-ibm-tiles': tileQualityFields(),
  'weather.check-era5-tiles': tileQualityFields(),
  'weather.load-ibm-historical': [
    DATE('StartDate', 'Start date'),
    DATE('EndDate', 'End date'),
  ],
  'weather.load-ibm-forecast': NO_FIELDS,
  'weather.calculate-ibm-reports': [
    DATE('StartDate', 'Start date'),
    DATE('EndDate', 'End date'),
    CHECK('SkipAggregates', 'SKIP_AGGREGATES'),
  ],
  'weather.generate-ibm-report-tiles': [
    DATE('StartDate', 'Start date'),
    DATE('EndDate', 'End date'),
    CHECK('ForceRecreate', 'FORCE_RECREATE'),
  ],
  'satellite.refresh-copernicus-metadata': [NUMBER('Take', 'TAKE_PRODUCTS')],
  'satellite.country-rgb-layers': [
    { name: 'ProductSourceType', type: 'select', labelKey: 'PRODUCT_SOURCE', options: SOURCE },
    DATE('StartDate', 'Start date'),
    DATE('EndDate', 'End date'),
    CHECK('PublishOnly', 'PUBLISH_ONLY'),
    CHECK('Overwrite', 'OVERWRITE_TIFF'),
    NUMBER('Take', 'TAKE_PRODUCTS'),
  ],
  'satellite.country-rgb-mosaic': [
    { name: 'ProductSourceType', type: 'select', labelKey: 'PRODUCT_SOURCE', options: SOURCE },
    NUMBER('Year', 'MOSAIC_YEAR'),
    CHECK('CurrentYearOnly', 'MOSAIC_CURRENT_YEAR_ONLY'),
  ],
  'satellite.index-tiff-ingest': [
    { name: 'ProductSourceType', type: 'select', labelKey: 'PRODUCT_SOURCE', options: SOURCE },
    TEXT('IndexRoot', 'INDEX_ROOT'),
  ],
  'satellite.index-tile-map-backfill': NO_FIELDS,
  'satellite.calculate-index-layers': NO_FIELDS,
  'satellite.reset-stale-work': NO_FIELDS,
  'satellite.repair-products': NO_FIELDS,
  'satellite.load-sentinel-metadata': [NUMBER('Take', 'TAKE_PRODUCTS')],
  'satellite.convert-sentinel-tiles': [NUMBER('Take', 'TAKE_PRODUCTS')],
  'satellite.find-landsat-products': ERA_DATES,
  'satellite.build-shared-index-reports': [NUMBER('WindowDays', 'WINDOW_DAYS')],
  'satellite.build-sentinel-preview': NO_FIELDS,
  'satellite.check-index-layers-data': [
    DATE('DateFrom', 'Start date'),
    DATE('DateTo', 'End date'),
    { name: 'IndexTypes', type: 'checks', labelKey: 'INDEX_TYPES', options: INDEX_TYPES },
    { name: 'RenderCheck', type: 'checkbox', labelKey: 'RENDER_CHECK', defaultValue: 'true' },
  ],
  'ai.start-segmentation': [{ name: 'Polygon', type: 'polygon', labelKey: 'SEGMENTATION_POLYGON', required: true }],
  'ai.identify-crops': [{ name: 'RegionId', type: 'regions', labelKey: 'Region', required: true }],
  'ai.calculate-yield-forecast': [{ name: 'RegionId', type: 'regions', labelKey: 'Region', required: true }],
  'ai.calculate-anomalies': [DATE('Date', 'ANOMALY_DATE'), TEXT('ModelId', 'MODEL_ID')],
  'ai.transfer-yield-forecast-to-seasons': NO_FIELDS,
  'ai.run-crop-classification': NO_FIELDS,
  'ai.classify-crops-onnx': [
    TEXT('PolygonDetectionJobId', 'DETECTION_JOB'),
    NUMBER('BatchSize', 'BATCH_SIZE'),
    TEXT('ModelId', 'MODEL_ID'),
  ],
  'ai.field-segmentation-net': [
    DATE('DateFrom', 'Start date'),
    DATE('DateTo', 'End date'),
    NUMBER('Count', 'TAKE_PRODUCTS'),
    TEXT('ModelId', 'MODEL_ID'),
    { name: 'RegionIds', type: 'regions', labelKey: 'Regions' },
    NUMBER('RegionLevel', 'FIELDSEG_REGION_LEVEL'),
    TEXT('RegionName', 'Region name'),
    CHECK('Overwrite', 'FIELDSEG_OVERWRITE'),
  ],
  'indices.detect-recurring-anomalies': [
    { name: 'FieldId', type: 'field', labelKey: 'Field' },
    TEXT('SeasonId', 'SEASON_ID'),
    NUMBER('DetectionYear', 'DETECTION_YEAR'),
  ],
  'analitic.collect-plant-diag-results': [
    NUMBER('MaxReportsPerRun', 'Max reports per run'),
    NUMBER('MaxDurationSeconds', 'MAX_DURATION_SECONDS'),
  ],
  'analitic.submit-bids-to-qwen': NO_FIELDS,
  'analitic.fetch-qwen-results': NO_FIELDS,
  'analitic.collect-qwen-full-analysis': NO_FIELDS,
  'analitic.start-qwen-analysis': NO_FIELDS,
  'analitic.request-farmer-report-for-finished-bids': NO_FIELDS,
  'analitic.request-farmer-report-for-satellite-anomalies': NO_FIELDS,
  'crop.sync-cadastre-parcels': NO_FIELDS,
  'crop.load-crop-agro-initial': [NUMBER('BatchSize', 'BATCH_SIZE'), NUMBER('MaxBatches', 'MAX_BATCHES')],
  'crop.apply-crop-agro-lands': [NUMBER('BatchSize', 'BATCH_SIZE'), NUMBER('MaxBatches', 'MAX_BATCHES')],
  'uav.pull-sources': NO_FIELDS,
  'system.create-partitions': NO_FIELDS,
  'system.health-check': NO_FIELDS,
  'system.process-due-user-notifications': NO_FIELDS,
};

function qualityFields(): ParameterFieldSchema[] {
  return [
    { name: 'RegionIds', type: 'regions', labelKey: 'Regions' },
    NUMBER('Lat', 'LATITUDE'),
    NUMBER('Lng', 'LONGITUDE'),
    DATE('StartDate', 'Start date'),
    DATE('EndDate', 'End date'),
    NUMBER('ForecastDaysAhead', 'FORECAST_DAYS_AHEAD'),
  ];
}

function tileQualityFields(): ParameterFieldSchema[] {
  return [
    ...qualityFields(),
    { name: 'RenderCheck', type: 'checkbox', labelKey: 'RENDER_CHECK', defaultValue: 'true' },
  ];
}

/** Field controls for a process. Null means this key keeps another start form. */
export function parameterFieldSchema(processKey: string | null | undefined): readonly ParameterFieldSchema[] | null {
  const key = (processKey ?? '').trim();
  if (!key) {
    return null;
  }
  if (Object.prototype.hasOwnProperty.call(BY_KEY, key)) {
    return BY_KEY[key];
  }
  if (key.startsWith('weather.')) {
    return NO_FIELDS;
  }
  return null;
}

export function defaultParameterFieldValues(
  schema: readonly ParameterFieldSchema[],
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of schema) {
    values[field.name] = field.defaultValue ?? (field.type === 'checkbox' ? 'false' : '');
  }
  return values;
}

export function parameterFieldValuesFromSource(
  schema: readonly ParameterFieldSchema[],
  source: Record<string, unknown>,
): Record<string, string> {
  const values = defaultParameterFieldValues(schema);
  for (const field of schema) {
    if (field.name === 'Lat' || field.name === 'Lng') {
      const point = firstPoint(source);
      if (point) {
        values['Lat'] = String(point.lat);
        values['Lng'] = String(point.lng);
      }
      continue;
    }
    const raw = readProperty(source, field.name);
    if (raw === undefined || raw === null) {
      continue;
    }
    if (field.type === 'checkbox') {
      values[field.name] = raw === true || raw === 'true' ? 'true' : 'false';
      continue;
    }
    if (Array.isArray(raw)) {
      values[field.name] = raw.map(item => String(item)).filter(Boolean).join(',');
      continue;
    }
    if (typeof raw === 'object') {
      values[field.name] = JSON.stringify(raw);
      continue;
    }
    values[field.name] = String(raw);
  }
  return values;
}

function readProperty(source: Record<string, unknown>, name: string): unknown {
  if (name in source) {
    return source[name];
  }
  const camel = name.charAt(0).toLowerCase() + name.slice(1);
  return source[camel];
}

function firstPoint(source: Record<string, unknown>): { lat: number; lng: number } | null {
  const points = readProperty(source, 'Points');
  if (!Array.isArray(points) || !points[0] || typeof points[0] !== 'object') {
    return null;
  }
  const point = points[0] as Record<string, unknown>;
  const lat = Number(point['Lat'] ?? point['lat']);
  const lng = Number(point['Lng'] ?? point['lng']);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return { lat, lng };
}
